/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { useEffect, useMemo, useState } from 'react';

export type Pagination<T> = {
  /** Items belonging to the current page. */
  pageItems: T[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  total: number;
  /** 1-based index of the first item on the page (e.g. 6 on page 2 of 5). */
  rangeStart: number;
  /** 1-based index of the last item on the page. */
  rangeEnd: number;
  /** True only when the list is longer than a single page. */
  showPager: boolean;
};

/**
 * Client-side, view-only pagination over an already-loaded array.
 *
 * Slices `items` into pages of `pageSize`. Nothing is fetched on page change —
 * this just changes which slice is rendered. The page is clamped whenever the
 * list shrinks (e.g. a streamed list that re-parses to fewer items) so it can
 * never point past the end.
 */
export function usePagination<T>(items: T[], pageSize = 5): Pagination<T> {
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const start = (page - 1) * pageSize;
  const pageItems = useMemo(
    () => items.slice(start, start + pageSize),
    [items, start, pageSize]
  );

  return {
    pageItems,
    page,
    setPage,
    totalPages,
    total: items.length,
    rangeStart: items.length === 0 ? 0 : start + 1,
    rangeEnd: Math.min(page * pageSize, items.length),
    showPager: items.length > pageSize,
  };
}
